/**
 * 本地存储工具
 * 安全地存储 Token 和用户数据
 */

const TOKEN_STORAGE_KEY = 'valuewarden_tokens';
const USER_STORAGE_KEY = 'valuewarden_user';
const VAULT_CACHE_KEY = 'valuewarden_vault_cache';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * 存储 Token
 */
export function storeTokens(tokens: StoredTokens): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    console.error('Failed to store tokens:', error);
  }
}

/**
 * 获取存储的 Token
 */
export function getStoredTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

/**
 * 清除 Token
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * 检查 Token 是否即将过期（5 分钟内）
 */
export function isTokenExpiring(tokens: StoredTokens): boolean {
  const fiveMinutes = 5 * 60 * 1000;
  return tokens.expiresAt - Date.now() < fiveMinutes;
}

/**
 * 存储用户信息
 */
export function storeUser(user: unknown): void {
  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Failed to store user:', error);
  }
}

/**
 * 获取存储的用户信息
 */
export function getStoredUser<T>(): T | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * 清除用户信息
 */
export function clearUser(): void {
  localStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * 缓存密码库数据（加密后的，可安全存储）
 */
export function cacheVaultData(data: unknown): void {
  try {
    sessionStorage.setItem(VAULT_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to cache vault data:', error);
  }
}

/**
 * 获取缓存的密码库数据
 */
export function getCachedVaultData<T>(): T | null {
  try {
    const raw = sessionStorage.getItem(VAULT_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * 清除密码库缓存
 */
export function clearVaultCache(): void {
  sessionStorage.removeItem(VAULT_CACHE_KEY);
}

/**
 * 清除所有本地数据（登出时调用）
 */
export function clearAll(): void {
  clearTokens();
  clearUser();
  clearVaultCache();
}
